import { useMsal } from '@azure/msal-react';
import { Button } from '@mui/material';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import React, { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { resetAuthInteractionCount } from '@/auth/tokenBroker';
import { loginRequest } from '@/authConfig';
// import useFreshworksWidget from '@/components/hooks/useFreshdesk';
import LoadingCircular from '@/components/LoadingCircular';
import WelcomeOnboardingTour from '@/components/WelcomeOnboardingTour';
import useAuth from '@/hooks/useAuth';
import { routeTree } from '@/routes';
import { useGlobalSettingsStore } from '@/store/useGlobalSettingsStore';
import { useGetGlobalSettings } from '@/features/identity/identity.services';

/*
interface RouterErrorBoundaryProps {
  children?: React.ReactNode;
}

// This component is used to throw the error when there are no children
const ErrorThrower: React.FC<{ error: Error }> = ({ error }) => {
  throw error;
};
function RouterErrorBoundary({ children }: RouterErrorBoundaryProps) {
  const error = useRouteError();

  const errorMessage = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : String(error);

  // Create an error object to pass to ErrorBoundary
  const errorObject = new Error(errorMessage);

  return (
    <ErrorBoundary>{children || <ErrorThrower error={errorObject} />}</ErrorBoundary>
  );
}
*/

// Create your route tree
const router = createRouter({ routeTree, context: { globalSettings: undefined! } });
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const App: React.FC = () => {
  const [isAuthenticated, isAuthenticateLoading, inProgress, authFatalError] = useAuth();
  const { instance } = useMsal();

  const { setGlobalSettings } = useGlobalSettingsStore(
    useShallow((state) => ({
      setGlobalSettings: state.setGlobalSettings
    }))
  );
  const shouldCallGetGlobalSettings = isAuthenticated && !isAuthenticateLoading && inProgress === 'none';
  const { data } = useGetGlobalSettings(shouldCallGetGlobalSettings);
  const routerContext = useMemo(() => ({ globalSettings: data }), [data]);

  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  useEffect(() => {
    if (data) {
      setGlobalSettings(data);
      router.invalidate();
    }
  }, [data, setGlobalSettings]);

  useEffect(() => {
    if (isAuthenticated !== null && !isAuthenticateLoading) {
      setIsAuthInitialized(true);
    }
  }, [isAuthenticateLoading]);

  // useFreshworksWidget(51000002765, isAuthenticated);

  if (!isAuthInitialized || isAuthenticateLoading || inProgress !== 'none') {
    return <LoadingCircular />;
  }

  if (authFatalError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FCFCF9] p-6">
        <div className="w-full max-w-xl rounded-xl border border-[#E6E6E6] bg-white p-6">
          <h1 className="mb-2 text-2xl font-semibold text-[#111111]">Sign-in error</h1>
          <p className="mb-6 text-sm text-[#4A4A4A]">{authFatalError.message}</p>
          <Button
            variant="contained"
            onClick={async () => {
              resetAuthInteractionCount();
              await instance.loginRedirect(loginRequest());
            }}
          >
            Sign in again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <RouterProvider router={router} context={routerContext} />
      {isAuthenticated && data && data.onboardingExperience !== 'none' && (
        <WelcomeOnboardingTour
          experience={data.onboardingExperience}
          returnBand={data.onboardingReturnBand}
          daysAway={data.onboardingDaysAway}
          nextRoute={data.onboardingNextRoute}
          isStrategyOwner={data.isStrategyOwner}
        />
      )}
    </>
  );
};

export default App;
